import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { GET_CLIMBER_BY_ID } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import type { Climber } from "../types/mmp-graphql";

interface ClimberByIdResponse {
  getClimberById: Climber[];
}

export interface ClimberWithUserPath extends Omit<Climber, "id" | "path"> {
  userID: string;
  userPath?: string;
  role?: string;
  riasec?: unknown;
  uniqueIdentifiers?: Array<{
    sectionID?: string | null;
    userPath?: string;
  }>;
}

interface QueryKey {
  id: string;
}

const fetchClimberById = async ({
  queryKey,
}: {
  queryKey: [string, QueryKey];
}): Promise<ClimberWithUserPath> => {
  const [, { id }] = queryKey;
  const { getClimberById } = await graphQLClient.request<ClimberByIdResponse>(
    GET_CLIMBER_BY_ID,
    {
      id,
    },
  );
  const climber = getClimberById[0];

  const userObj: ClimberWithUserPath = {
    userID: climber.id,
    sub: climber.sub,
    firstName: climber.firstName,
    lastName: climber.lastName,
    username: climber.username,
    email: climber.email,
    shardID: climber.shardID,
    xp: climber.xp,
    preferredID: climber.preferredID,
    userPath: climber.path,
    ladderProgress: climber.ladderProgress,
    facets: climber.facets,
    profileChoices: climber.profileChoices,
    receivedSurvey: climber.receivedSurvey,
    careerPlan: climber.careerPlan,
    coursePlan: climber.coursePlan,
    uniqueIdentifiers: getClimberById.map(({ parentID, path }) => ({
      sectionID: parentID,
      userPath: path,
    })),
  };
  return userObj;
};

export const useGetClimberById = (
  id: string | undefined,
  enabled: boolean = true,
): UseQueryResult<ClimberWithUserPath, Error> => {
  return useQuery({
    queryKey: ["climber", { id: id || "" }],
    queryFn: fetchClimberById,
    enabled: !!id && enabled,
  });
};
